import { cn } from "../../lib/cn";
import type { BadgeTone } from "./Badge";

/**
 * The app's semantic status vocabulary, in one place.
 *
 * Before this, message status lived in two duplicated `Record<string, BadgeTone>` maps (the
 * dashboard's and messaging's), while device, contact, API-key and webhook status were each
 * inlined as one-off `<Badge tone="...">` calls across five pages. Nothing enforced that
 * "Active" meant the same colour on Contacts as on API Keys, and nothing caught a status the
 * design had no opinion about.
 *
 * Tones map to intent, not to literal colour:
 *   success — a terminal good outcome, or a healthy resting state
 *   accent  — in flight; something is actively happening
 *   warn    — needs attention but is not a failure
 *   danger  — failed, revoked, or blocked
 *   neutral — inert, queued, or simply off
 *
 * `accent` is the orange used for primary actions and active nav, so it is spent sparingly:
 * only genuinely in-flight states get it. A table full of orange pills would make the accent
 * mean nothing anywhere else in the product.
 */
export type StatusKind =
  // message lifecycle
  | "PENDING" | "QUEUED" | "SENDING" | "SENT" | "DELIVERED" | "FAILED" | "CANCELLED"
  // device
  | "ONLINE" | "OFFLINE"
  // api key
  | "ACTIVE" | "REVOKED" | "EXPIRED"
  // contact
  | "OPTED_OUT"
  // webhook
  | "DISABLED";

interface StatusMeta {
  tone: BadgeTone;
  label: string;
}

const STATUS: Record<StatusKind, StatusMeta> = {
  PENDING: { tone: "neutral", label: "Pending" },
  QUEUED: { tone: "neutral", label: "Queued" },
  SENDING: { tone: "accent", label: "Sending" },
  SENT: { tone: "accent", label: "Sent" },
  DELIVERED: { tone: "success", label: "Delivered" },
  FAILED: { tone: "danger", label: "Failed" },
  CANCELLED: { tone: "warn", label: "Cancelled" },
  ONLINE: { tone: "success", label: "Online" },
  OFFLINE: { tone: "neutral", label: "Offline" },
  ACTIVE: { tone: "success", label: "Active" },
  REVOKED: { tone: "danger", label: "Revoked" },
  EXPIRED: { tone: "warn", label: "Expired" },
  OPTED_OUT: { tone: "danger", label: "Opted out" },
  DISABLED: { tone: "neutral", label: "Disabled" },
};

const toneClasses: Record<BadgeTone, string> = {
  neutral: "bg-surface-hover text-text-muted ring-border",
  success: "bg-success-soft text-success ring-success/20",
  warn: "bg-warn-soft text-warn ring-warn/20",
  danger: "bg-danger-soft text-[var(--sb-danger-on-soft)] ring-danger/20",
  accent: "bg-accent-soft text-accent ring-accent/20",
};

const dotClasses: Record<BadgeTone, string> = {
  neutral: "bg-text-muted",
  success: "bg-success",
  warn: "bg-warn",
  danger: "bg-danger",
  accent: "bg-accent",
};

export interface StatusPillProps {
  status: StatusKind | string;
  className?: string;
}

/**
 * One pill, one size, everywhere. Fixed height and radius so a column of pills has a straight
 * edge; a 1px ring instead of a heavier fill so a dense table does not turn into a colour chart.
 *
 * The leading dot carries the same information as the colour, which keeps the state legible for
 * a red/green colour-blind reader and at a glance in peripheral vision.
 */
export function StatusPill({ status, className }: StatusPillProps) {
  const meta = STATUS[status as StatusKind] ?? {
    tone: "neutral" as BadgeTone,
    // An unmapped status still renders rather than throwing — but tidied, so an unexpected
    // SCREAMING_CASE value from the API does not look like a different design language.
    label: String(status).toLowerCase().replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()),
  };

  return (
    <span
      className={cn(
        "inline-flex h-6 items-center gap-1.5 rounded-pill px-2.5",
        "text-xs font-medium leading-none whitespace-nowrap",
        "ring-1 ring-inset",
        toneClasses[meta.tone],
        className,
      )}
    >
      <span className={cn("size-1.5 shrink-0 rounded-pill", dotClasses[meta.tone])} aria-hidden />
      {meta.label}
    </span>
  );
}

/** The tone a given status resolves to, for callers that need to colour something else to match. */
export function statusTone(status: StatusKind | string): BadgeTone {
  return STATUS[status as StatusKind]?.tone ?? "neutral";
}
